import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { isSecretEvent } from '../../services/eventCategories';
import secretImg from '../../assets/images/secret.png';
import { logAnalyticsEvent } from '../../analytics/logAnalyticsEvent';
import LikeButton from '../LikeButton/LikeButton';
import {
  formatEventAgeNote,
  formatEventDateLine,
  formatEventOrganizer,
  formatEventPrice,
  getEventPublicCategories,
} from '../../services/eventDisplayFields';
import { pauseOtherEventAudio } from '../../services/eventAudio';
import {
  hasEditorialFirePick,
} from '../../services/eventEditorialPick';
import { outboundEventHref } from '../../services/eventPublicPoster';
import { recordEventClicked, recordEventSeen } from '../../services/eventStats';
import { eventDetailUrl, getSiteBaseUrl } from '../../services/seoSchema';
import { EventLocationControl } from './EventLocationMapModal';
import {
  getEventDisplayTitle,
  isInstagramCtaTitle,
  isInstagramOnlyEvent,
} from '../../services/instagramEventDisplay';
import { formatFriendsGoingLabel } from '../../services/userProfile';
import { getEventImageSrc } from '../../services/eventImageSrc';
import '../../styles/public.css';

export { getEventDisplayTitle, isInstagramCtaTitle, isInstagramOnlyEvent, getEventImageSrc };

function useCurrentUser() {
  try {
    const auth = useAuth();
    return auth?.currentUser || null;
  } catch (_) {
    return null;
  }
}

function buildEventImageAlt(event) {
  const title = getEventDisplayTitle(event);
  const location = event?.location ? ` at ${event.location}` : ' in Sarajevo';
  return `${title}${location}`;
}

/** Simple Instagram glyph for the hover badge. */
const InstagramLogoIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 2.2c3.2 0 3.6 0 4.9.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 3.2-1.6 4.8-4.9 4.9-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9C2.4 3.9 3.9 2.4 7.1 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.2 0-3.5 0-4.8.1-2.3.1-3.4 1.2-3.5 3.5-.1 1.2-.1 1.6-.1 4.8s0 3.5.1 4.8c.1 2.3 1.2 3.4 3.5 3.5 1.2.1 1.6.1 4.8.1s3.5 0 4.8-.1c2.3-.1 3.4-1.2 3.5-3.5.1-1.2.1-1.6.1-4.8s0-3.5-.1-4.8c-.1-2.3-1.2-3.4-3.5-3.5-1.3-.1-1.6-.1-4.8-.1zm0 3.2a4.8 4.8 0 1 1 0 9.6 4.8 4.8 0 0 1 0-9.6zm0 7.9a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2zm5.1-8.2a1.1 1.1 0 1 1 0-2.3 1.1 1.1 0 0 1 0 2.3z"
    />
  </svg>
);

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"
    />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z" />
  </svg>
);

/**
 * Click-to-play song control. Stops propagation so it does not open the event.
 * Only one `.event-audio` plays at a time.
 */
export const EventAudioControl = ({ event, variant = 'icon' }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const url = typeof event?.audioUrl === 'string' ? event.audioUrl.trim() : '';
  const title = typeof event?.audioTitle === 'string' ? event.audioTitle.trim() : '';

  useEffect(() => {
    setPlaying(false);
  }, [url]);

  if (!url) return null;

  const stopCardOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const togglePlay = (e) => {
    stopCardOpen(e);
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      pauseOtherEventAudio(el);
      try {
        const playPromise = el.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (_) {
        /* jsdom / autoplay policy */
      }
    } else {
      el.pause();
    }
  };

  if (variant === 'full') {
    return (
      <div className="event-pub-audio event-pub-audio--full" onClick={stopCardOpen}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          className="event-audio"
          src={url}
          preload="metadata"
          controls
          onPlay={(e) => {
            pauseOtherEventAudio(e.currentTarget);
            setPlaying(true);
          }}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
        {title ? <p className="event-pub-audio__title pub-mono">{title}</p> : null}
      </div>
    );
  }

  return (
    <div className="event-pub-audio event-pub-audio--icon" onClick={stopCardOpen}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        className="event-audio"
        src={url}
        preload="none"
        onPlay={(e) => {
          pauseOtherEventAudio(e.currentTarget);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        type="button"
        className="event-pub-audio__btn"
        onClick={togglePlay}
        aria-label={playing ? `Pause ${title || 'song'}` : `Play ${title || 'song'}`}
        title={title || (playing ? 'Pause' : 'Play song')}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
    </div>
  );
};

/** Native share when available; otherwise copy link and show "Copied!" feedback. */
export const ShareEventButton = ({ event, variant = 'icon' }) => {
  const [copied, setCopied] = useState(false);
  const url = event?.id ? eventDetailUrl(event.id) : getSiteBaseUrl();
  const title = getEventDisplayTitle(event);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const markCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (canNativeShare) {
      try {
        await navigator.share({
          title,
          text: title,
          url,
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // Fall through to clipboard if share fails for other reasons
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      markCopied();
    } catch {
      // Clipboard unavailable — ignore
    }
  };

  return (
    <button
      type="button"
      className={`event-share-btn event-share-btn--${variant}`}
      onClick={handleShare}
      aria-label={copied ? 'Link copied' : canNativeShare ? 'Share event' : 'Copy event link'}
      title={copied ? 'Copied!' : 'Share'}
    >
      {variant === 'icon' ? (copied ? '✓' : <ShareIcon />) : (copied ? 'Copied!' : 'Share Event')}
    </button>
  );
};

/** Editorial pick (highest) + content category pills — card, modal, and detail page. */
export const EventCategoryPills = ({ event }) => {
  const categories = getEventPublicCategories(event);
  if (categories.length === 0) return null;

  return (
    <div className="event-categories">
      {categories.map((category) => (
        <span key={category} className="event-category-pill">
          {category}
        </span>
      ))}
    </div>
  );
};

/** Outbound tickets link + share — shared by expanded card, modal, and detail page. */
export const EventCardActions = ({ event, shareVariant = 'full', LikeControl = LikeButton }) => {
  const outbound = outboundEventHref(event);
  const igOnly = isInstagramOnlyEvent(event);
  // Instagram-only: card/image click opens IG — don't show a written outbound link.
  const showOutbound = outbound && !igOnly;

  return (
    <div className="pub-event-detail-actions event-pub-card-actions">
      <LikeControl event={event} />
      {showOutbound && (
        <a
          href={outbound}
          className="pub-event-detail-ext"
          target="_blank"
          rel="noopener noreferrer"
        >
          Event details &amp; tickets →
        </a>
      )}
      <ShareEventButton event={event} variant={shareVariant} />
    </div>
  );
};

/** Poster + optional overlay actions (like/share sit on the image, outside the card link). */
export const EventCardMedia = ({
  event,
  actions = null,
  MediaLink = null,
  mediaLinkProps = null,
  imagePriority = false,
  imageVariant = 'card',
}) => {
  const currentUser = useCurrentUser();
  const isSecret = isSecretEvent(event) && !currentUser;
  const imgSrc = getEventImageSrc(event, { variant: imageVariant });
  const imageAlt = buildEventImageAlt(event);
  const igOnly = isInstagramOnlyEvent(event);

  const media = (
    <div className={igOnly ? 'event-pub-media event-pub-media--instagram' : 'event-pub-media'}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={isSecret ? 'Secret event' : imageAlt}
          className={isSecret ? 'event-pub-img event-pub-img--secret' : 'event-pub-img'}
          loading={imagePriority ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={imagePriority ? 'high' : 'low'}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div className="event-pub-img-placeholder" aria-hidden="true">🎉</div>
      )}
      {isSecret && (
        <div className="event-pub-secret-overlay">
          <img src={secretImg} alt="Secret" className="event-pub-secret-img" />
        </div>
      )}
      {igOnly && !isSecret && (
        <span className="event-pub-ig-badge" title="From Instagram">
          <InstagramLogoIcon />
        </span>
      )}
    </div>
  );

  return (
    <div className="event-pub-media-wrap">
      {MediaLink ? (
        <MediaLink {...mediaLinkProps}>{media}</MediaLink>
      ) : (
        media
      )}
      {actions}
    </div>
  );
};

/** Text block under the poster — date, title, location, categories, description. */
export const EventCardBody = ({ event, expanded = false }) => {
  const currentUser = useCurrentUser();
  const isSecret = isSecretEvent(event) && !currentUser;
  const dateLine = formatEventDateLine(event);
  const displayTitle = getEventDisplayTitle(event);
  const igOnly = isInstagramOnlyEvent(event);
  const price = formatEventPrice(event);
  const organizer = formatEventOrganizer(event);
  const ageNote = formatEventAgeNote(event);

  const titleClass = [
    'event-pub-title',
    igOnly ? 'event-pub-title--instagram' : '',
    isSecret ? 'event-pub-title--secret' : '',
  ].filter(Boolean).join(' ');
  const showTitle = !isInstagramCtaTitle(displayTitle);

  return (
    <div className="event-pub-body">
      {dateLine && (
        <time className="event-pub-date" dateTime={event.dateStr}>
          {dateLine}
        </time>
      )}
      {showTitle ? (
        <div className="event-pub-title-wrap">
          <h3
            className={titleClass}
            title={isSecret ? 'Sign in to see event title' : (igOnly ? (event.instagramUrl || event.posterLink || displayTitle) : displayTitle)}
          >
            {displayTitle}
          </h3>
          {isSecret && (
            <div className="event-pub-title-secret-overlay pub-mono">
              Sign in to see
            </div>
          )}
        </div>
      ) : null}
      {(price || organizer || ageNote) && (
        <div className="event-pub-meta pub-mono">
          {organizer ? <span className="event-pub-meta__item">{organizer}</span> : null}
          {price ? <span className="event-pub-meta__item">{price}</span> : null}
          {ageNote ? <span className="event-pub-meta__item event-pub-meta__item--age">{ageNote}</span> : null}
        </div>
      )}
      {event.location && (
        <div className="event-pub-location">
          <span>📍</span>
          {isSecret ? (
            <span>Secret</span>
          ) : expanded ? (
            <EventLocationControl event={event} />
          ) : (
            <span>{event.location}</span>
          )}
        </div>
      )}
      <EventCategoryPills event={event} />
      {event.description && (
        <p className={expanded ? 'event-pub-desc event-pub-desc--full' : 'event-pub-desc'}>
          {event.description}
        </p>
      )}
      {expanded ? <EventAudioControl event={event} variant="full" /> : null}
    </div>
  );
};

/** Shared card body — small (clamped) vs large/expanded (full description). */
const EventCardContent = ({ event, expanded = false }) => (
  <>
    <EventCardMedia event={event} />
    <EventCardBody event={event} expanded={expanded} />
  </>
);

/** Small card — links to the event page; onOpen opens the modal on the homepage. */
export const EventCard = ({
  event,
  onOpen,
  friendsGoing = [],
  imagePriority = false,
  LikeControl = LikeButton,
}) => {
  const currentUser = useCurrentUser();
  const { openAuthModal } = useAuthModal();
  const isSecret = isSecretEvent(event) && !currentUser;
  const detailPath = event?.id ? `/events/${encodeURIComponent(event.id)}` : '/#events';
  const displayTitle = getEventDisplayTitle(event);
  const igOnly = isInstagramOnlyEvent(event);
  const imageSrc = getEventImageSrc(event, { variant: 'full' });
  const friendsGoingLabel = formatFriendsGoingLabel(friendsGoing);

  const cardRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressDoneRef = useRef(false);
  const LONG_PRESS_MS = 1500;

  const clearLongPress = () => {
    if (longPressTimerRef.current != null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const openEventImage = () => {
    if (!imageSrc || !/^https?:\/\//i.test(imageSrc)) return false;
    window.open(imageSrc, '_blank', 'noopener,noreferrer');
    return true;
  };

  const startLongPress = (e) => {
    if (!igOnly || !imageSrc) return;
    if (e.button != null && e.button !== 0) return;
    longPressDoneRef.current = false;
    clearLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressDoneRef.current = true;
      openEventImage();
      logAnalyticsEvent('event_card_long_press_image', {
        event_id: event?.id || '',
        event_name: displayTitle || 'unknown',
      });
    }, LONG_PRESS_MS);
  };

  const handleClick = (e) => {
    if (longPressDoneRef.current) {
      e.preventDefault();
      longPressDoneRef.current = false;
      return;
    }

    logAnalyticsEvent('event_card_click', {
      event_id: event?.id || '',
      event_name: displayTitle || 'unknown',
      event_date: event?.dateStr || '',
      event_location: event?.location || '',
      open_mode: onOpen ? 'modal' : 'page',
      instagram_event: igOnly,
    });
    recordEventClicked(event?.id, { title: displayTitle, source: 'list' });

    if (isSecret) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      openAuthModal({
        mode: 'signin',
        onSuccess: typeof onOpen === 'function' ? () => onOpen(event) : undefined,
      });
      return;
    }

    if (!onOpen) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onOpen(event);
  };

  useEffect(() => () => clearLongPress(), []);

  useEffect(() => {
    const el = cardRef.current;
    const id = event?.id;
    if (!el || !id || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5
        );
        if (!visible) return;
        recordEventSeen(id, { title: displayTitle, source: 'list' });
        observer.disconnect();
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [event?.id, displayTitle]);

  const showFire = hasEditorialFirePick(event.editorialPick, event);
  const cardClass = [
    'event-pub-card',
    'event-pub-card--clickable',
    igOnly ? 'event-pub-card--instagram' : '',
    showFire ? 'event-pub-card--fire' : '',
  ].filter(Boolean).join(' ');

  const longPressHandlers = igOnly
    ? {
        onPointerDown: startLongPress,
        onPointerUp: clearLongPress,
        onPointerLeave: clearLongPress,
        onPointerCancel: clearLongPress,
        onContextMenu: (e) => {
          // Suppress OS menu after a successful long-press open
          if (longPressDoneRef.current) e.preventDefault();
        },
      }
    : {};

  const mediaLinkProps = {
    className: 'event-pub-card__media-link',
    to: detailPath,
    onClick: handleClick,
    // Body trigger is the accessible control; keep poster out of tab order and
    // block click-focus so it never retains focus under aria-hidden ancestors.
    tabIndex: -1,
    onMouseDown: (e) => {
      e.preventDefault();
    },
    ...(igOnly
      ? {
          title: 'Tap: event details · Hold 1.5s: event image',
          ...longPressHandlers,
        }
      : {}),
  };

  const bodyLinkProps = {
    className: 'event-pub-card__trigger',
    to: detailPath,
    onClick: handleClick,
    'aria-label': `View details for ${displayTitle}`,
    ...(igOnly
      ? {
          title: 'Tap: event details · Hold 1.5s: event image',
          ...longPressHandlers,
        }
      : {}),
  };

  const mediaActions = (
    <div className="event-pub-card__media-actions">
      <EventAudioControl event={event} variant="icon" />
      <div className="event-pub-card__media-actions-end">
        <LikeControl event={event} />
      </div>
    </div>
  );

  return (
    <article ref={cardRef} className={cardClass}>
      <EventCardMedia
        event={event}
        actions={mediaActions}
        MediaLink={Link}
        mediaLinkProps={mediaLinkProps}
        imagePriority={imagePriority}
      />
      <Link {...bodyLinkProps}>
        <EventCardBody event={event} />
      </Link>
      {friendsGoingLabel && (
        <span
          className="event-pub-card__friends-going pub-mono"
          title={friendsGoing.map((friend) => friend.displayName).join(', ')}
        >
          {friendsGoingLabel}
        </span>
      )}
    </article>
  );
};
